// SAMPLE data for the mastery tracks that have no real signal yet.
//
// The coding track is live (LG-17): it reads `dashboard.mastery.path`, built
// from `TopicDefinition`/`SubtopicDefinition` and this client's
// `ClientSubtopicState`. System design and behavioral have no taxonomy and no
// mastery state anywhere in the product, so their views run on the invented
// numbers below and the widget labels those tabs as a preview. Anything here
// is illustrative — never cite it as a client's progress.

import {
  type MasteryTopicStatus,
  masteryTopicStatus,
} from '@/lib/coaching/mastery-path'

export interface Subtopic {
  name: string
  mastered: boolean
}

/** The live coding map's five states, shared so the preview cannot drift. */
export type TopicStatus = MasteryTopicStatus

/**
 * The live map's own ladder, so the preview really does read the same. The
 * sample tracks carry no attempt timestamps, so the one simplification is
 * visible here at the call site: "started" can only mean "something is
 * already mastered".
 */
export function topicStatus(
  subtopics: Subtopic[],
  prereqsMet: boolean,
): TopicStatus {
  const done = subtopics.filter((st) => st.mastered).length
  return masteryTopicStatus({
    masteredCount: done,
    totalCount: subtopics.length,
    started: done > 0,
    prerequisitesMet: prereqsMet,
  })
}

/** Sample subtopics: the first `masteredCount` of `names` read as mastered. */
const s = (names: string[], masteredCount: number): Subtopic[] =>
  names.map((name, i) => ({ name, mastered: i < masteredCount }))

// ---------------------------------------------------------------------------
// Behavioral: talent tree
// ---------------------------------------------------------------------------

export interface Talent {
  id: string
  name: string
  /** Short code shown on locked/unfilled nodes */
  code: string
  subtopics: Subtopic[]
}

export interface TalentTier {
  tier: number
  talents: Talent[]
}

export interface TalentSpec {
  id: string
  name: string
  tiers: TalentTier[]
}

export const BEHAVIORAL_SPECS: TalentSpec[] = [
  {
    id: 'story',
    name: 'Your Story',
    tiers: [
      {
        tier: 1,
        talents: [
          {
            id: 'tmay',
            name: 'Tell Me About Yourself',
            code: 'TM',
            subtopics: s(
              [
                '60-second version',
                '2-minute version',
                'Tailoring to the role',
              ],
              3,
            ),
          },
          {
            id: 'why-company',
            name: 'Why This Company',
            code: 'WC',
            subtopics: s(
              ['Research framework', 'Genuine motivation', 'Role fit'],
              3,
            ),
          },
        ],
      },
      {
        tier: 2,
        talents: [
          {
            id: 'fav-project',
            name: 'Favorite Project',
            code: 'FP',
            subtopics: s(
              [
                'Project selection',
                'Technical depth',
                'Impact numbers',
                'Trade-off story',
              ],
              2,
            ),
          },
          {
            id: 'career-walk',
            name: 'Career Walkthrough',
            code: 'CW',
            subtopics: s(
              ['Transition narratives', 'Growth arc', 'Gap handling'],
              1,
            ),
          },
        ],
      },
      {
        tier: 3,
        talents: [
          {
            id: 'vision',
            name: 'Where You Are Headed',
            code: 'WH',
            subtopics: s(
              ['5-year framing', 'Ambition vs fit', 'Follow-up handling'],
              0,
            ),
          },
        ],
      },
    ],
  },
  {
    id: 'teamwork',
    name: 'Teamwork & Conflict',
    tiers: [
      {
        tier: 1,
        talents: [
          {
            id: 'conflict',
            name: 'A Recent Conflict',
            code: 'RC',
            subtopics: s(
              ['STAR structure', 'Neutral framing', 'Resolution & learning'],
              3,
            ),
          },
          {
            id: 'feedback',
            name: 'Receiving Feedback',
            code: 'RF',
            subtopics: s(
              ['Hard feedback story', 'Behavior change', 'Follow-through'],
              2,
            ),
          },
        ],
      },
      {
        tier: 2,
        talents: [
          {
            id: 'difficult-teammate',
            name: 'Difficult Teammate',
            code: 'DT',
            subtopics: s(
              ['Empathy framing', 'Escalation judgment', 'Outcome story'],
              1,
            ),
          },
          {
            id: 'disagree-commit',
            name: 'Disagree & Commit',
            code: 'DC',
            subtopics: s(
              [
                'Strong opinions story',
                'Commitment proof',
                'When you were wrong',
              ],
              0,
            ),
          },
        ],
      },
      {
        tier: 3,
        talents: [
          {
            id: 'cross-team',
            name: 'Cross-Team Influence',
            code: 'CT',
            subtopics: s(
              [
                'Alignment story',
                'Influence without authority',
                'Stakeholder mapping',
              ],
              0,
            ),
          },
        ],
      },
    ],
  },
  {
    id: 'leadership',
    name: 'Leadership & Growth',
    tiers: [
      {
        tier: 1,
        talents: [
          {
            id: 'failure',
            name: 'A Real Failure',
            code: 'RF',
            subtopics: s(
              ['Genuine stakes', 'Ownership language', 'What changed after'],
              2,
            ),
          },
          {
            id: 'ambiguity',
            name: 'Handling Ambiguity',
            code: 'HA',
            subtopics: s(
              [
                'Scoping story',
                'Deciding with partial info',
                'Communicating uncertainty',
              ],
              1,
            ),
          },
        ],
      },
      {
        tier: 2,
        talents: [
          {
            id: 'leading',
            name: 'Leading a Project',
            code: 'LP',
            subtopics: s(
              [
                'Kickoff & alignment',
                'Unblocking others',
                'Delivery story',
                'Retro & learnings',
              ],
              0,
            ),
          },
          {
            id: 'mentoring',
            name: 'Mentoring Others',
            code: 'MO',
            subtopics: s(
              ['Growth story', 'Adapting your style', 'Measurable outcome'],
              0,
            ),
          },
        ],
      },
      {
        tier: 3,
        talents: [
          {
            id: 'hard-call',
            name: 'The Hard Call',
            code: 'HC',
            subtopics: s(
              [
                'High-stakes decision',
                'Trade-off narrative',
                'Aftermath honesty',
              ],
              0,
            ),
          },
        ],
      },
    ],
  },
]

/** A tier unlocks when every talent in all lower tiers is fully mastered. */
export function tierLocked(spec: TalentSpec, tier: number): boolean {
  return spec.tiers
    .filter((t) => t.tier < tier)
    .some((t) =>
      t.talents.some((talent) => talent.subtopics.some((st) => !st.mastered)),
    )
}

// ---------------------------------------------------------------------------
// System design: tier ladder ("Stage N of 5")
// ---------------------------------------------------------------------------

export interface LadderTopic {
  id: string
  name: string
  subtopics: Subtopic[]
  /** Locked until earlier stage work unlocks it */
  locked: boolean
}

export interface LadderStage {
  id: string
  name: string
  description: string
  topics: LadderTopic[]
  current?: boolean
  comingSoon?: boolean
}

const lt = (
  id: string,
  name: string,
  names: string[],
  masteredCount: number,
  locked = false,
): LadderTopic => ({ id, name, subtopics: s(names, masteredCount), locked })

export const SYSTEM_DESIGN_LADDER: LadderStage[] = [
  {
    id: 'foundations',
    name: 'Foundations',
    description: 'The vocabulary every design conversation builds on.',
    topics: [
      lt(
        'http',
        'HTTP & Networking',
        ['Request lifecycle', 'TCP vs UDP', 'TLS basics', 'DNS'],
        4,
      ),
      lt(
        'latency',
        'Latency Budgets',
        ['Latency numbers', 'Percentiles', 'Tail latency'],
        3,
      ),
      lt(
        'data-modeling',
        'Data Modeling Basics',
        ['Entities & relations', 'Indexes', 'Access patterns'],
        3,
      ),
      lt(
        'estimation',
        'Back-of-Envelope Estimation',
        ['Powers of two', 'QPS math', 'Storage sizing', 'Bandwidth math'],
        2,
      ),
    ],
  },
  {
    id: 'key-tech',
    name: 'Key technologies',
    description:
      'The building blocks you will reach for in nearly every design.',
    current: true,
    topics: [
      lt(
        'caching',
        'Caching',
        [
          'Cache-aside',
          'Write-through vs write-back',
          'Eviction policies',
          'CDN caching',
          'Cache invalidation',
        ],
        3,
      ),
      lt(
        'load-balancing',
        'Load Balancing',
        ['L4 vs L7', 'Algorithms', 'Health checks', 'Sticky sessions'],
        1,
      ),
      lt(
        'sql-vs-nosql',
        'SQL vs NoSQL Trade-offs',
        [
          'Normalization trade-offs',
          'Document stores',
          'Wide-column stores',
          'Choosing under constraints',
        ],
        0,
      ),
      lt(
        'queues-streams',
        'Queues & Streams',
        [
          'Message queues',
          'Pub/sub',
          'Stream processing',
          'Delivery guarantees',
        ],
        0,
        true,
      ),
      lt(
        'replication',
        'Replication & Sharding',
        [
          'Leader-follower',
          'Multi-leader',
          'Partitioning schemes',
          'Rebalancing',
        ],
        0,
        true,
      ),
    ],
  },
  {
    id: 'baseline',
    name: 'Question baseline',
    description:
      'A scored mock question to find exactly where you stand before drilling.',
    topics: [
      lt(
        'baseline-q',
        'Baseline Assessment',
        ['Timed mock question', 'Rubric scoring', 'Gap report'],
        0,
        true,
      ),
    ],
  },
  {
    id: 'practice',
    name: 'Question practice',
    description: 'Classic questions, drilled until the patterns are automatic.',
    topics: [
      lt(
        'url-shortener',
        'Design a URL Shortener',
        ['Requirements', 'High-level design', 'Deep dives', 'Scaling'],
        0,
        true,
      ),
      lt(
        'news-feed',
        'Design a News Feed',
        ['Fan-out strategies', 'Ranking', 'Storage', 'Caching strategy'],
        0,
        true,
      ),
      lt(
        'chat-system',
        'Design a Chat System',
        ['Connections', 'Message delivery', 'Presence', 'History'],
        0,
        true,
      ),
      lt(
        'rate-limiter',
        'Design a Rate Limiter',
        ['Algorithms', 'Distributed counting', 'Failure modes'],
        0,
        true,
      ),
    ],
  },
  {
    id: 'company-prep',
    name: 'Company-specific prep',
    description:
      'Interview formats, rubrics, and favorite questions for your target companies.',
    comingSoon: true,
    topics: [],
  },
]

export function ladderStagePct(stage: LadderStage): number {
  const all = stage.topics.flatMap((t) => t.subtopics)
  if (all.length === 0) return 0
  return Math.round((all.filter((st) => st.mastered).length / all.length) * 100)
}

// ---------------------------------------------------------------------------
// Extra credit: cross-track "because you know... you can start" suggestions
// ---------------------------------------------------------------------------

export type Track = 'coding' | 'system-design' | 'behavioral'

export interface ExtraCreditSuggestion {
  id: string
  track: Track
  name: string
  /** Names of mastered prerequisites that opened this up */
  becauseYouKnow: string[]
  subtopics: Subtopic[]
  /** What finishing this might unlock later */
  unlocksHint: string
}

export const EXTRA_CREDIT_SUGGESTIONS: ExtraCreditSuggestion[] = [
  {
    id: 'linked-lists',
    track: 'coding',
    name: 'Linked Lists',
    becauseYouKnow: ['Two Pointers'],
    subtopics: s(
      ['Traversal', 'Reversal', 'Cycle detection', 'Merging', 'Dummy heads'],
      1,
    ),
    unlocksHint: 'unlocks Trees later',
  },
  {
    id: 'caching',
    track: 'system-design',
    name: 'Caching',
    becauseYouKnow: ['HTTP & Networking', 'Latency Budgets'],
    subtopics: s(
      [
        'Cache-aside',
        'Write-through vs write-back',
        'Eviction policies',
        'CDN caching',
        'Cache invalidation',
      ],
      3,
    ),
    unlocksHint: 'unlocks Replication & Sharding later',
  },
  {
    id: 'fav-project',
    track: 'behavioral',
    name: 'Favorite Project',
    becauseYouKnow: ['Tell Me About Yourself'],
    subtopics: s(
      [
        'Project selection',
        'Technical depth',
        'Impact numbers',
        'Trade-off story',
      ],
      2,
    ),
    unlocksHint: 'unlocks Where You Are Headed later',
  },
]
